import { c as _c } from "react/compiler-runtime";
import { differenceInDays } from 'date-fns';
import Fuse from 'fuse.js';
import { uniq } from 'lodash-es';
import { useMemo } from 'react';
import type { Key } from 'react-aria-components';
import { create } from 'zustand';
import type { PricedBlueprint } from '~/components/BlueprintIndex/BlueprintIndexSearch/BlueprintIndexSearch';
import type { AllBlueprints } from '~/libs/dato/queries/blueprints';
import type { ListYearRoundPricingByDate } from '~/services/engine-ts';
import type { CMSBlueprint } from '~/types/cms/Blueprint.types';
import { usePlaygroundPreferences } from './playgroundStore';
export type Sort = 'relevance' | 'price-desc' | 'price-asc' | 'price-per-night-desc' | 'price-per-night-asc' | 'duration-desc' | 'duration-asc';
export type BpiFilter = 'budget' | 'mid-range' | 'higher-range' | 'luxe' | 'long-weekend' | 'week' | 'two_weeks' | 'longer' | 'jai_sleeper_train';
export type BlueprintIndexState = {
  sort: Sort;
  filters: BpiFilter[];
  destinations: Set<string>;
  startDate: string | null; // asatDate
  pricedBlueprints: PricedBlueprint[];
  // UI
  loading: boolean;
};
export type BlueprintIndexActions = {
  setSort: (sort: Key & Sort) => void;
  setFilters: (filter: BpiFilter[]) => void;
  setDestinations: (destinations: Set<string>) => void;
  setStartDate: (date: string | null) => void; // asatDate
  setPricedBlueprints: (blueprints: PricedBlueprint[]) => void;
  setLoading: (loading: boolean) => void;
};
export type BlueprintIndexStore = BlueprintIndexState & BlueprintIndexActions;
const defaultState: BlueprintIndexState = {
  sort: 'relevance',
  loading: true,
  filters: [],
  destinations: new Set(),
  startDate: null,
  pricedBlueprints: []
};
export const useBlueprintIndexStore = create<BlueprintIndexStore>()(set => ({
  ...defaultState,
  setSort: newSort => set({
    sort: newSort
  }),
  setFilters: newFilters => set({
    filters: newFilters
  }),
  setStartDate: newDate => set({
    startDate: newDate
  }),
  setPricedBlueprints: blueprints => set({
    pricedBlueprints: blueprints
  }),
  setDestinations: destinations => set({
    destinations
  }),
  setLoading: loading => set({
    loading
  })
}));

// Selector hooks for common state access patterns
export const useBlueprintIndexSort = () => {
  return useBlueprintIndexStore(_temp);
};
export const useBlueprintIndexFilters = () => {
  return useBlueprintIndexStore(_temp2);
};
export const useBlueprintIndexDestinations = () => {
  return useBlueprintIndexStore(_temp3);
};
export const useBlueprintIndexStartDate = () => {
  return useBlueprintIndexStore(_temp4);
};
export const useBlueprintIndexPricedBlueprints = () => {
  return useBlueprintIndexStore(_temp5);
};
export const useBlueprintIndexLoading = () => {
  return useBlueprintIndexStore(_temp6);
};
export const useBlueprintIndexActions = () => {
  return useBlueprintIndexStore(_temp7);
};

// Utility functions
export const getDestinationNamesFromCmsBlueprints = (blueprints: AllBlueprints): string[] => {
  const countries = blueprints.flatMap(blueprint => blueprint.country?.split(', '));
  const places = blueprints.flatMap(blueprint => blueprint.places?.split(', '));
  const regions = blueprints.flatMap(blueprint => blueprint.region);
  const destinations = uniq([...countries, ...places, ...regions].filter((d): d is string => Boolean(d)));
  return destinations;
};
export type DestinationSearchTerm = {
  destination: string;
  id: string;
};
export function useDestinationSearch(blueprints, filterValue) {
  const $ = _c(12);
  let t0;
  if ($[0] !== blueprints) {
    t0 = blueprints ?? [];
    $[0] = blueprints;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = getDestinationNamesFromCmsBlueprints(t0);
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const allSearchTerms = t1;
  let t2;
  if ($[4] !== allSearchTerms) {
    t2 = new Fuse(allSearchTerms, {
      keys: ["filterValue"],
      threshold: 0.4
    });
    $[4] = allSearchTerms;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const fuse = t2;
  let t3;
  if ($[6] !== filterValue || $[7] !== fuse) {
    t3 = fuse.search(filterValue).map(_temp8);
    $[6] = filterValue;
    $[7] = fuse;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const results = t3;
  let t4;
  if ($[9] !== filterValue || $[10] !== results) {
    t4 = results.length ? results : filterValue ? [{
      destination: filterValue,
      id: "no-results"
    }] : [];
    $[9] = filterValue;
    $[10] = results;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function _temp8(item) {
  return {
    destination: item.item,
    id: item.refIndex.toString()
  };
}
export function useBlueprintSearch(blueprintsWithPrice, t0) {
  const $ = _c(8);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedDestinations = t1;
  let t2;
  if ($[2] !== blueprintsWithPrice) {
    t2 = new Fuse(blueprintsWithPrice, {
      keys: [{
        name: "title"
      }, {
        name: "places"
      }, {
        name: "region"
      }, {
        name: "country"
      }],
      threshold: 0.2
    });
    $[2] = blueprintsWithPrice;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const fuse = t2;
  if (selectedDestinations.length === 0) {
    return blueprintsWithPrice;
  }
  let t3;
  if ($[4] !== blueprintsWithPrice || $[5] !== fuse || $[6] !== selectedDestinations[0]) {
    const searchTerm = selectedDestinations[0].toLowerCase();
    const fuseResults = fuse.search(searchTerm);
    const resultsWithScores = fuseResults.map(_temp9);
    const substringMatches = blueprintsWithPrice.filter(blueprint => blueprint.searchTerms.toLowerCase().includes(searchTerm)).map(_temp10);
    const combinedResults = [...resultsWithScores, ...substringMatches].filter(_temp11);
    combinedResults.sort(_temp12);
    t3 = combinedResults.map(_temp13);
    $[4] = blueprintsWithPrice;
    $[5] = fuse;
    $[6] = selectedDestinations[0];
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp13(result_0) {
  return result_0.item;
}
function _temp12(a, b) {
  const blueprintScoreDifference = a.item.score - b.item.score;
  if (blueprintScoreDifference !== 0) {
    return blueprintScoreDifference;
  }
  const priceDifference = b.item.price - a.item.price;
  if (priceDifference !== 0) {
    return priceDifference;
  }
  return (a.fuseScore ?? 0) - (b.fuseScore ?? 0);
}
function _temp11(value, index, self) {
  return index === self.findIndex(t => t.item.blueprintId === value.item.blueprintId);
}
function _temp10(item) {
  return {
    item,
    fuseScore: 0
  };
}
function _temp9(result) {
  return {
    item: result.item,
    fuseScore: result.score
  };
}
export const sortBlueprintsDefault = (blueprints: PricedBlueprint[]) => {
  return blueprints.sort((a, b) => {
    // handles falsy values and puts them last
    const scoreA = a.score ?? Infinity;
    const scoreB = b.score ?? Infinity;
    const blueprintScoreDifference = scoreA - scoreB;
    if (blueprintScoreDifference !== 0) return blueprintScoreDifference;
    const priceA = a.price ?? Infinity;
    const priceB = b.price ?? Infinity;
    const blueprintPriceDifference = priceB - priceA;
    if (blueprintPriceDifference !== 0) return blueprintPriceDifference;
    return 0;
  });
};
export const useFilteredBlueprints = () => {
  const $ = _c(8);
  const pricedBlueprints = useBlueprintIndexStore(_temp14);
  const {
    filters,
    destinations: selectedDestinations
  } = useBlueprintIndexStore(_temp15);
  const filterBlueprint = _temp16;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = (blueprints, filters_1) => {
      const budgetFilters = ["budget", "mid_range", "higher_end", "luxe"].filter(filter_2 => filters_1.includes(filter_2));
      const durationFilters = ["long_weekend", "week", "two_weeks", "longer"].filter(filter_3 => filters_1.includes(filter_3));
      const themeFilters = filters_1.filter(_temp17);
      const parsedFilters = {
        priceRanges: budgetFilters,
        durationRanges: durationFilters,
        themes: themeFilters
      };
      return blueprints.filter(blueprint_0 => filterBlueprint(blueprint_0, parsedFilters));
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const filterBlueprints = t0;
  let t1;
  if ($[1] !== pricedBlueprints) {
    t1 = pricedBlueprints ?? [];
    $[1] = pricedBlueprints;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== selectedDestinations) {
    t2 = Array.from(selectedDestinations);
    $[3] = selectedDestinations;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = useBlueprintSearch(t1, t2);
  let t4;
  if ($[5] !== filters || $[6] !== t3) {
    t4 = filterBlueprints(t3, filters);
    $[5] = filters;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
const getSelectedDuration = (nights: number | null, startDate: string | null, endDate: string | null) => {
  if (nights) return nights;
  if (startDate && endDate) return differenceInDays(new Date(endDate), new Date(startDate));
  return 0;
};
export const useSortedBlueprints = () => {
  const $ = _c(15);
  const sort = useBlueprintIndexStore(_temp18);
  const {
    nights,
    startDate,
    endDate
  } = usePlaygroundPreferences();
  const filteredBlueprints = useFilteredBlueprints();
  let t0;
  if ($[0] !== endDate || $[1] !== filteredBlueprints || $[2] !== nights || $[3] !== sort || $[4] !== startDate) {
    let t1;
    if ($[6] !== endDate || $[7] !== nights || $[8] !== sort || $[9] !== startDate) {
      t1 = (a, b) => {
        switch (sort) {
          case "relevance":
            {
              if (startDate && endDate || nights) {
                const scoreA = a.score ?? Infinity;
                const scoreB = b.score ?? Infinity;
                const scoreDiff = scoreA - scoreB;
                if (scoreDiff !== 0) {
                  return scoreDiff;
                }
                const selectedDuration = getSelectedDuration(nights, startDate, endDate);
                if (a.duration === selectedDuration && b.duration !== selectedDuration) {
                  return -1;
                }
                if (b.duration === selectedDuration && a.duration !== selectedDuration) {
                  return 1;
                }
                const aDiff = Math.abs(a.duration - selectedDuration);
                const bDiff = Math.abs(b.duration - selectedDuration);
                if (aDiff === bDiff) {
                  return b.duration - a.duration;
                }
                return aDiff - bDiff;
              }
              return 0;
            }
          case "price-desc":
            {
              return b.price - a.price;
            }
          case "price-asc":
            {
              return a.price - b.price;
            }
          case "price-per-night-desc":
            {
              return b.pricePerNight - a.pricePerNight;
            }
          case "price-per-night-asc":
            {
              return a.pricePerNight - b.pricePerNight;
            }
          case "duration-desc":
            {
              return b.duration - a.duration;
            }
          case "duration-asc":
            {
              return a.duration - b.duration;
            }
          default:
            {
              return 0;
            }
        }
      };
      $[6] = endDate;
      $[7] = nights;
      $[8] = sort;
      $[9] = startDate;
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    let t2;
    if ($[11] !== endDate || $[12] !== nights || $[13] !== startDate) {
      t2 = blueprint => {
        if (startDate && endDate || nights) {
          const selectedDuration_0 = getSelectedDuration(nights, startDate, endDate);
          if (Math.abs(blueprint.duration - selectedDuration_0) <= 1) {
            return true;
          }
          return false;
        }
        return true;
      };
      $[11] = endDate;
      $[12] = nights;
      $[13] = startDate;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    t0 = filteredBlueprints.sort(t1).filter(t2);
    $[0] = endDate;
    $[1] = filteredBlueprints;
    $[2] = nights;
    $[3] = sort;
    $[4] = startDate;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
};
export const addPricesToBlueprints = (prices: ListYearRoundPricingByDate[], blueprints: AllBlueprints) => {
  return blueprints.map(blueprint => {
    const match = prices.find(blueprintPriceObj => blueprint.blueprintId === blueprintPriceObj.journeyUniqueId);
    if (!match || match.concatenatedStatus) return null;
    return {
      ...blueprint,
      price: match.totalPrepayAmount,
      totalPrepayAmountUS: match.totalPrepayAmountUS,
      journeyUniqueId: match.journeyUniqueId,
      startDate: match.startDate,
      pricePerNight: match?.pricePerNight ?? Math.round(match.totalPrepayAmount / (blueprint?.duration ?? 1) * 100) / 100
    };
  }).filter(blueprint => blueprint !== null);
};
function _temp(state) {
  return state.sort;
}
function _temp2(state) {
  return state.filters;
}
function _temp3(state) {
  return state.destinations;
}
function _temp4(state) {
  return state.startDate;
}
function _temp5(state) {
  return state.pricedBlueprints;
}
function _temp6(state) {
  return state.loading;
}
function _temp7(state) {
  return {
    setSort: state.setSort,
    setFilters: state.setFilters,
    setDestinations: state.setDestinations,
    setStartDate: state.setStartDate,
    setPricedBlueprints: state.setPricedBlueprints,
    setLoading: state.setLoading
  };
}
function _temp14(state) {
  return state.pricedBlueprints;
}
function _temp15(state_0) {
  return state_0;
}
function _temp16(blueprint, filters_0) {
  const isMatchedBudget = filters_0.priceRanges.length ? filters_0.priceRanges.some(filter => blueprint.priceRange.includes(filter)) : true;
  const isMatchedDuration = filters_0.durationRanges.length ? filters_0.durationRanges.some(filter_0 => blueprint.durationRange.includes(filter_0)) : true;
  const isMatchedInterest = filters_0.themes.length ? filters_0.themes.some(filter_1 => blueprint.tags.some(tag => tag.tagId.includes(filter_1))) : true;
  return isMatchedBudget && isMatchedDuration && isMatchedInterest;
}
function _temp17(filter_4) {
  return ["jai_attribute_adventure", "jai_attribute_arts_and_culture", "jai_ferry", "jai_attribute_food_and_drink", "jai_attribute_good_for_kids", "jai_attribute_good_for_vegans", "jai_attribute_history", "jai_attribute_nature", "jai_attribute_nightlife", "jai_attribute_relaxation", "ski", "jai_sleeper_train"].includes(filter_4);
}
function _temp18(state) {
  return state.sort;
}